'use client';

import { useMemo } from 'react';
import { ThemeProvider } from 'next-themes';
import { NuqsAdapter } from 'nuqs/adapters/next/app';

import type { FeatureFlagsConfig } from '@kit/feature-flags';
import { FeatureFlagsProvider, useFeatureFlags } from '@kit/feature-flags';
import { I18nProvider } from '@kit/i18n/provider';
import { MonitoringProvider } from '@kit/monitoring/components';
import { AppEventsProvider } from '@kit/shared/events';
import { If } from '@kit/ui/if';
import { VersionUpdater } from '@kit/ui/version-updater';

import { GlobalSearchProvider } from '~/(adaptive)/_components/search/global-search-provider';
import { AnalyticsProvider } from '~/components/analytics-provider';
import { AuthProvider } from '~/components/auth-provider';
import { BrandProvider } from '~/components/brand-provider';
import appConfig from '~/config/app.config';
import type { Brand } from '~/lib/brand';
import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { getI18nSettings } from '~/lib/i18n/i18n.settings';
import { ReactQueryProvider } from './react-query-provider';

type RootProvidersProps = React.PropsWithChildren<{
  // The language to use for the app (optional)
  lang?: string;
  // The theme (light or dark or system) (optional)
  theme?: string;
  // The CSP nonce to pass to scripts (optional)
  nonce?: string;
  // The brand context (ballee or flow)
  brand?: Brand;
  // Feature flags from database
  featureFlags?: FeatureFlagsConfig;
}>;

export function RootProviders({
  lang,
  theme = appConfig.theme,
  nonce,
  brand = 'flow',
  featureFlags,
  children,
}: RootProvidersProps) {
  const i18nSettings = useMemo(() => getI18nSettings(lang), [lang]);

  return (
    <NuqsAdapter>
      <MonitoringProvider>
        <AppEventsProvider>
          <AnalyticsProvider>
            <ReactQueryProvider>
              <FeatureFlagsProvider flags={featureFlags}>
                <I18nProvider settings={i18nSettings} resolver={i18nResolver}>
                  <AuthProvider>
                    <BrandProvider brand={brand}>
                      <ThemeProvider
                        attribute="class"
                        enableSystem
                        disableTransitionOnChange
                        defaultTheme={theme}
                        storageKey="theme"
                        nonce={nonce}
                      >
                        <GlobalSearchProvider>{children}</GlobalSearchProvider>
                      </ThemeProvider>
                    </BrandProvider>
                  </AuthProvider>

                  <VersionUpdaterWrapper />
                </I18nProvider>
              </FeatureFlagsProvider>
            </ReactQueryProvider>
          </AnalyticsProvider>
        </AppEventsProvider>
      </MonitoringProvider>
    </NuqsAdapter>
  );
}

/**
 * Wrapper component that uses feature flags hook to conditionally render VersionUpdater
 */
function VersionUpdaterWrapper() {
  const flags = useFeatureFlags();

  return (
    <If condition={flags.enableVersionUpdater}>
      <VersionUpdater />
    </If>
  );
}
